import link from "../static/text/link";

function normalizePageSource(pageSource) {
  if (Array.isArray(pageSource)) {
    return pageSource[pageSource.length - 1] || "";
  }
  if (pageSource == null) {
    return "";
  }
  return String(pageSource);
}

function sourceKey(pageSource) {
  return String(normalizePageSource(pageSource) || "")
    .trim()
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");
}

function pathOnly(asPath) {
  return String(asPath || "").split("?")[0].split("#")[0];
}

function firstValue(value) {
  if (Array.isArray(value)) return value[value.length - 1] || "";
  if (value == null) return "";
  return value;
}

function stripLocalePrefix(pathname) {
  const path = pathOnly(pathname);
  const stripped = path.replace(/^\/(id|en)(?=\/|$)/, "");
  return stripped || "/";
}

function inferSourceFromPath(pathname) {
  const path = stripLocalePrefix(pathname);
  if (path === "/plan" || path.indexOf("/plan/") === 0) return "plan";
  if (path === "/promo" || path.indexOf("/promo/") === 0) return "promo";
  if (path === "/premium" || path === "/" || path.indexOf("/premium/") === 0) {
    return "premium";
  }
  if (path.indexOf("/c/") === 0 || path.indexOf("/collab") === 0) return "collab";
  if (path.indexOf("/flashdeals") === 0) return "flashdeals";
  if (path.indexOf("/flashsale") === 0) return "flashsale";
  return "";
}

function inferSlugFromPath(pathname) {
  const path = stripLocalePrefix(pathname);
  if (path.indexOf("/c/") === 0) {
    return path.split("/")[2] || "";
  }
  return "";
}

/**
 * Live semi-prod often ships landing pages with empty pageProps
 * (GIP throw → _app catch). Custom-server query + URL still have the
 * real source. CTA buttons must read those, not only React props.
 */
function resolveLandingCtaContext({
  pageSource,
  additionalProps,
  query,
  pathname,
} = {}) {
  const props = additionalProps || {};
  const q = query || {};
  const source = sourceKey(
    pageSource
    || props.page_source
    || q.page_source
    || inferSourceFromPath(pathname)
  );
  const slug = firstValue(props.slug || q.slug || inferSlugFromPath(pathname));
  const landingType = firstValue(props.landing_type || q.landing_type);
  const contentType = firstValue(props.content_type || q.content_type);
  return {
    pageSource: source,
    additionalProps: {
      ...props,
      page_source: source || props.page_source,
      slug,
      landing_type: landingType,
      content_type: contentType || (source === "collab" ? "collab" : contentType),
      pkg: firstValue(props.pkg || q.pkg),
      c: firstValue(props.c || q.c),
      cms_slug: firstValue(props.cms_slug || q.cms_slug),
      auto_apply: firstValue(props.auto_apply || q.auto_apply),
      promo_price: props.promo_price != null ? props.promo_price : q.promo_price,
    },
  };
}

function followLandingCta(url) {
  if (!url || typeof window === "undefined") return;
  if (url.charAt(0) === "#") {
    const el = document.getElementById(url.slice(1));
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      if (window.history && window.history.replaceState) {
        const next = `${window.location.pathname}${window.location.search}${url}`;
        window.history.replaceState(null, "", next);
      }
      return;
    }
  }
  window.location.href = url;
}

function getLandingQuestionnaireUrl(pageSource, additionalProps) {
  const props = additionalProps || {};
  const source = sourceKey(pageSource || props.page_source);
  const {
    c,
    limited_offer,
    reseller_price,
    promo_price,
    userPremiumStatus,
    content_type,
    slug: slugProp,
    pkg,
    landing_type,
    auto_apply,
    cms_slug,
  } = props;
  let slug = slugProp;
  if (!slug && cms_slug && String(cms_slug).indexOf("/c/") === 0) {
    slug = String(cms_slug).replace(/^\/c\//, "");
  }

  // Trello 399 14 AUG 26-2 #3: /plan has packages on the landing. No quiz.
  if (source === "plan") {
    return "#plan";
  }
  // Trello 399 14 AUG 26-2 #2: /promo must open ITS OWN questionnaire.
  if (source === "promo") {
    return "/promo/questionnaire";
  }
  // [Trello 398 26 AUG 26 item 5] The early return is what keeps a premium /
  // homepage CTA on /premium/questionnaire instead of being re-based to
  // /promo|/limited-offer|/hargaspesial by the promo_price / limited_offer /
  // reseller_price flags in the generic tail below. It must NOT also swallow the
  // affiliate code: without `c`, questionnaire-v2 SSRs with no promo_id and the
  // scratch coupon falls back to the generic max % and the date-derived code.
  if (source === "premium") {
    const premiumParams = new URLSearchParams();
    if (c) premiumParams.set("c", c);
    const premiumQuery = premiumParams.toString() ? `?${premiumParams.toString()}` : "";
    return `/premium/questionnaire${premiumQuery}`;
  }
  if (source === "flashsale" || source === "/flashsale") {
    return "/flashsales/quiz";
  }
  if (source === "flashdeals") {
    return "#plan";
  }
  if (userPremiumStatus && userPremiumStatus.is_active) {
    return link.mobile.packageList(c, promo_price);
  }
  if (source === "redeem") {
    return "/premium/redeem-voucher";
  }
  if (source === "special") {
    return "/special/redeem-voucher";
  }
  if (source === "deals") {
    return "/deals/questionnaire";
  }
  if (source === "diskon") {
    return "/diskon/questionnaire";
  }
  if (content_type === "collab" || source === "collab") {
    if (slug) {
      const params = new URLSearchParams();
      params.set("page_source", "collab");
      params.set("slug", slug);
      params.set("cms_slug", `/c/${slug}`);
      if (pkg) params.set("pkg", pkg);
      if (c) params.set("c", c);
      if (landing_type === "without_questionnaire") {
        params.set("from", "collab");
        params.set("target", "package-list");
        params.set("landing_type", "without_questionnaire");
        if (auto_apply) params.set("auto_apply", auto_apply);
        return `/premium/redeem-voucher?${params.toString()}`;
      }
      params.set("landing_type", "with_questionnaire");
      return `/collab/questionnaire?${params.toString()}`;
    }
    return "/collab-voucher";
  }

  let baseUrl = "/premium";
  if (limited_offer) {
    baseUrl = "/limited-offer";
  } else if (reseller_price) {
    baseUrl = "/hargaspesial";
  } else if (promo_price) {
    baseUrl = "/promo";
  } else if (source && String(pageSource || "").charAt(0) === "/") {
    baseUrl = `/${source}`;
  } else if (source) {
    baseUrl = `/${source}`;
  }

  const params = new URLSearchParams();
  if (c) params.set("c", c);
  if (cms_slug) params.set("cms_slug", cms_slug);
  const queryString = params.toString() ? `?${params.toString()}` : "";

  return `${baseUrl}/questionnaire${queryString}`;
}

export {
  normalizePageSource,
  sourceKey,
  resolveLandingCtaContext,
  followLandingCta,
  getLandingQuestionnaireUrl,
};
